/**
 * คู่มือ & Flow ระบบ — ผังขั้นตอนการทำงานมาตรฐานของระบบ (Non-HR UX §2).
 *
 * สรุป workflow หลักเป็นผังสเต็ป + ป้ายบทบาทคนรับผิดชอบแต่ละขั้น เพื่อให้ทุกคน
 * เห็นภาพรวมว่างานไหลยังไง "เดินตามรอยเดียวกัน แม้มุมมองต่างกัน". แต่ละสเต็ป
 * กดเข้าหน้าจริงได้ (ถ้าเป็นงานฝั่ง admin).
 *
 * v1 = flow มาตรฐาน. ถัดไป: ปรับให้สะท้อนการตั้งค่าจริงของบริษัท (เช่น จำนวน
 * ชั้นอนุมัติจาก approval chain).
 */
import { Link } from 'react-router-dom';
import { ChevronRight, UserPlus, CalendarCheck, Banknote } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useWorkflowConfig, type WorkflowConfig } from '../../hooks';

type Role = 'admin' | 'finance' | 'supervisor' | 'employee';

const ROLE_LABEL: Record<Role, string> = {
  admin: 'ผู้ดูแลระบบ',
  finance: 'บัญชี/การเงิน',
  supervisor: 'หัวหน้างาน',
  employee: 'พนักงาน',
};
const ROLE_CHIP: Record<Role, string> = {
  admin: 'bg-slate-100 text-slate-700',
  finance: 'bg-teal-50 text-teal-700',
  supervisor: 'bg-amber-50 text-amber-700',
  employee: 'bg-green-50 text-green-700',
};

interface Step { title: string; role: Role; to?: string }
interface Flow { icon: typeof UserPlus; title: string; steps: Step[] }

/** ค่า default ตอนยังโหลด config ไม่เสร็จ (มาตรฐานทั่วไป) */
const DEFAULT_CFG: WorkflowConfig = {
  approvalLevels: 1,
  approverLevels: [{ label: 'หัวหน้างาน', roleKind: 'supervisor' }],
  usesShifts: true,
  usesCommission: false,
  usesLeavePackages: true,
};

/** สร้างผังตาม config จริงของบริษัท */
function buildFlows(cfg: WorkflowConfig): Flow[] {
  // รับพนักงานใหม่ — มีสเต็ปมอบกะ/ตั้งสิทธิ์ลา เฉพาะถ้าบริษัทใช้
  const onboard: Step[] = [
    { title: 'สร้างทะเบียนพนักงาน', role: 'admin', to: '/hr/people#onboard' },
    { title: 'กำหนดตำแหน่ง & เงินเดือน', role: 'finance', to: '/hr/people' },
  ];
  if (cfg.usesShifts) onboard.push({ title: 'มอบหมายกะ/ตารางงาน', role: 'supervisor', to: '/hr/time#shifts' });
  if (cfg.usesLeavePackages) onboard.push({ title: 'ตั้งสิทธิ์การลา', role: 'admin', to: '/hr/quotas' });
  onboard.push({ title: 'พนักงานยืนยันข้อมูล (ผูกบัญชี LINE)', role: 'employee' });
  onboard.push({ title: 'เริ่มเข้าระบบเงินเดือน', role: 'finance', to: '/owner/cycle' });

  // การลา — จำนวนชั้น + role ผู้อนุมัติแต่ละชั้น ตามคนจริงใน approval chain
  const leave: Step[] = [{ title: 'พนักงานยื่นใบลา', role: 'employee' }];
  const n = Math.max(1, Math.min(3, cfg.approvalLevels));
  for (let i = 0; i < n; i++) {
    const ap = cfg.approverLevels[i] ?? { label: i === 0 ? 'หัวหน้างาน' : 'ผู้อนุมัติ', roleKind: 'supervisor' as const };
    leave.push({ title: `${ap.label} อนุมัติ`, role: ap.roleKind, to: '/approvals' });
  }
  leave.push({ title: 'HR ตรวจสิทธิวันลา', role: 'admin', to: '/hr/quotas' });
  leave.push({ title: 'บันทึกเข้าระบบเวลา', role: 'admin', to: '/hr/time' });

  // รอบเงินเดือน — มีสเต็ปคำนวณคอม เฉพาะถ้าใช้คอมมิชชั่น
  const payroll: Step[] = [
    { title: 'ปิดรอบเวลา & OT', role: 'admin', to: '/owner/cycle#reconcile' },
    { title: 'รวมการลา/เบิก', role: 'admin', to: '/owner/cycle#reconcile' },
  ];
  if (cfg.usesCommission) payroll.push({ title: 'คำนวณคอมมิชชั่น', role: 'finance', to: '/hr/commission' });
  payroll.push({ title: 'คำนวณเงินเดือน', role: 'finance', to: '/owner/cycle#payroll' });
  payroll.push({ title: 'ตรวจสอบ & อนุมัติ', role: 'finance', to: '/owner/cycle#payroll' });
  payroll.push({ title: 'จ่าย & ออกสลิป', role: 'finance', to: '/owner/cycle#payroll' });
  payroll.push({ title: 'นำส่งภาษี/สปส.', role: 'finance', to: '/owner/wage#year-end' });

  return [
    { icon: UserPlus, title: 'รับพนักงานใหม่', steps: onboard },
    { icon: CalendarCheck, title: 'การลา → อนุมัติ → บันทึก', steps: leave },
    { icon: Banknote, title: 'รอบเงินเดือนรายเดือน', steps: payroll },
  ];
}

function StepCard({ step, index }: { step: Step; index: number }) {
  const body = (
    <div className="w-44 shrink-0 rounded-xl border border-gray-200 bg-white p-3 hover:border-brand-300 hover:shadow-sm transition h-full">
      <div className="text-[11px] font-semibold text-gray-400 tracking-wide">STEP {index + 1}</div>
      <div className="text-sm font-medium text-gray-900 mt-0.5 leading-snug">{step.title}</div>
      <span className={`badge mt-2 ${ROLE_CHIP[step.role]}`}>{ROLE_LABEL[step.role]}</span>
    </div>
  );
  return step.to ? <Link to={step.to} className="block h-full">{body}</Link> : body;
}

function FlowCard({ flow }: { flow: Flow }) {
  const Icon = flow.icon;
  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-brand-600" />
        </span>
        <h2 className="font-display font-semibold text-gray-900">{flow.title}</h2>
        <span className="text-xs text-gray-400">· {flow.steps.length} ขั้นตอน</span>
      </div>
      <div className="flex items-stretch gap-1 overflow-x-auto pb-1 -mx-1 px-1">
        {flow.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-1 shrink-0">
            <StepCard step={s} index={i} />
            {i < flow.steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
          </div>
        ))}
      </div>
    </section>
  );
}

export function WorkflowGuide() {
  const { data: cfg } = useWorkflowConfig();
  const flows = buildFlows(cfg ?? DEFAULT_CFG);

  return (
    <AdminLayout title="ขั้นตอนการทำงาน" subtitle="ผังขั้นตอนการทำงานของบริษัทคุณ — ปรับตามการตั้งค่าจริง">
      <div className="space-y-5 max-w-5xl">
        {/* คำอธิบายป้ายบทบาท + หมายเหตุว่าปรับตาม config จริง */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>ป้ายสี = คนรับผิดชอบ:</span>
          {(['admin', 'finance', 'supervisor', 'employee'] as Role[]).map((r) => (
            <span key={r} className={`badge ${ROLE_CHIP[r]}`}>{ROLE_LABEL[r]}</span>
          ))}
        </div>
        {cfg && (
          <p className="text-xs text-gray-500">
            ผังนี้สะท้อนการตั้งค่าของบริษัทคุณ: อนุมัติ {cfg.approvalLevels} ชั้น
            {cfg.usesShifts ? ' · ใช้กะ' : ' · ไม่ใช้กะ'}
            {cfg.usesCommission ? ' · มีคอมมิชชั่น' : ''}
            {' '}— เปลี่ยนการตั้งค่าแล้วผังจะปรับตาม
          </p>
        )}

        {flows.map((f) => <FlowCard key={f.title} flow={f} />)}

        <p className="text-xs text-gray-400">แตะสเต็ปที่เป็นงานฝั่งผู้ดูแล/การเงิน เพื่อไปยังหน้าทำงานจริงได้ทันที</p>
      </div>
    </AdminLayout>
  );
}
